import { ApiService } from "./api.service";
import type { TTimelineResponse } from "@/types/timeline-view";

/**
 * Work-item filters the timeline endpoints accept — the same param names and
 * comma-separated value semantics as the work-item list endpoints, so the
 * query built from the shared filter state can be sent as-is.
 */
export type TTimelineFilterParams = {
  /** Temporary events-polish bridge; demo provenance must not imply a synced calendar. */
  seeded_events_editable?: "true";
  /** Comma-separated WorkItemState ids. */
  state?: string;
  /** Priority option code (e.g. "high"). */
  priority?: string;
  /** Comma-separated assignee user ids. */
  assignees?: string;
  /** Comma-separated task-category (WorkspaceCategoryValue) ids — narrows tasks only. */
  task_category_ids?: string;
  /** Comma-separated event-category (WorkspaceCategoryValue) ids — narrows events only. */
  event_category_ids?: string;
  /** Comma-separated project ids — narrows both kinds. */
  project_ids?: string;
  /** Comma-separated ProjectState ids — keeps only items whose project is in one
   *  of those statuses. The workspace's CONFIGURABLE project status, never the
   *  derived on-track / overdue / over-capacity health band (NG-1637). */
  project_state_ids?: string;
  /** Case-insensitive substring match on the work-item name. */
  search?: string;
  /** Tri-state archived narrowing: "true" (archived only) / "false" (active
   *  only); omit to show both (the global/work-item timelines' default). */
  archived?: string;
  /** Inclusive `task.target_date` window (YYYY-MM-DD) — narrows tasks only. */
  target_date_after?: string;
  target_date_before?: string;
  /**
   * Repeatable custom-field filters, each formatted `<field_id>:<option_value>`
   * (select / multiselect only). Sent as repeated `custom_field` query params.
   */
  custom_field?: string[];
  /**
   * Which visibility rule the WORKSPACE timeline applies (NG-1804). `"list"`
   * asks for the same rows the Events list returns — including unassigned
   * items and items assigned outside the viewer's teams — instead of the
   * legacy team-scoped set. Omitted means the legacy rule, which is what the
   * flag-off bundle must keep sending.
   *
   * A bridge, not a filter: the backend holds no LaunchDarkly SDK, so this is
   * how `events-polish` reaches it. Delete the field, and the `visibility`
   * param, when that flag goes on permanently. The project and work-item
   * timelines ignore it.
   */
  visibility?: "list";
};

function buildTimelineParams(
  start: string,
  end: string,
  filters?: TTimelineFilterParams,
): URLSearchParams {
  const params = new URLSearchParams({ start, end });
  for (const [key, value] of Object.entries(filters ?? {})) {
    if (Array.isArray(value)) {
      // Repeatable param (e.g. custom_field): one query entry per value.
      for (const entry of value) if (entry) params.append(key, entry);
    } else if (value) {
      params.set(key, value);
    }
  }
  return params;
}

export class TimelineService extends ApiService {
  async getTimeline(
    workspaceSlug: string,
    start: string,
    end: string,
    filters?: TTimelineFilterParams,
  ): Promise<TTimelineResponse> {
    const params = buildTimelineParams(start, end, filters);
    return this.get(`/api/workspaces/${workspaceSlug}/timeline/?${params.toString()}`);
  }

  async getProjectTimeline(
    workspaceSlug: string,
    projectId: string,
    start: string,
    end: string,
    filters?: TTimelineFilterParams,
  ): Promise<TTimelineResponse> {
    const params = buildTimelineParams(start, end, filters);
    return this.get(
      `/api/workspaces/${workspaceSlug}/projects/${projectId}/timeline/?${params.toString()}`,
    );
  }

  /** Subtree-scoped timeline: a work item plus its descendants (event detail tab). */
  async getWorkItemTimeline(
    workspaceSlug: string,
    workItemId: string,
    start: string,
    end: string,
    filters?: TTimelineFilterParams,
  ): Promise<TTimelineResponse> {
    const params = buildTimelineParams(start, end, filters);
    return this.get(
      `/api/workspaces/${workspaceSlug}/work-items/${workItemId}/timeline/?${params.toString()}`,
    );
  }
}
